// © 2026 AttainIP Engineering
/**
 * HalmosAnimation — Lottie-based Halmos symbol animation for thinking states.
 * Two variants: "white" (maroon stroke on transparent) and "red" (white stroke on transparent).
 */

import { useEffect, useRef } from 'react';
import lottie, { type AnimationItem } from 'lottie-web';
import halmosWhiteData from '../assets/lottie/halmos-white.json';
import halmosRedData from '../assets/lottie/halmos-red.json';

interface HalmosAnimationProps {
  /** "white" = maroon stroke (for chat thread), "red" = white stroke (for send button),
   *  "grey" = desaturated maroon, used for generic loading spinners across the app. */
  variant?: 'white' | 'red' | 'grey';
  /** Width/height in pixels (default: 24) */
  size?: number;
  className?: string;
}

export function HalmosAnimation({ variant = 'white', size = 24, className }: HalmosAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // 'grey' reuses the maroon-stroke data and desaturates via CSS filter,
    // so we don't need a third Lottie file.
    animRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      animationData: variant === 'red' ? halmosRedData : halmosWhiteData,
    });
    animRef.current.setSpeed(0.7);

    return () => {
      animRef.current?.destroy();
    };
  }, [variant]);

  const filter = variant === 'grey' ? 'grayscale(1) opacity(0.55)' : undefined;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: size, height: size, filter }}
      aria-label="Processing"
      role="status"
    />
  );
}
